import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import CustomDropdown from '@components/CustomDropdown';
import NoData from '@components/NoData';
import { toastError } from '@components/Toast';

import CollectionCard from '@features/buyer-request/components/collection/CollectionCard';
import OrderCard from '@features/buyer-request/components/order/OrderCard';
import QuotesCard from '@features/buyer-request/components/quote/QuotesCard';
import SampleCard from '@features/buyer-request/components/sample/SampleCard';
import TeamChip from '@features/filter/Common/TeamChip';
import { generateTeamParams, isTeamFilterExist } from '@features/filter/static/filterUtil';
import UsersForTeamLead from '@features/filter/v1/core/UsersForTeamLead';

import {
	clearBuyerRequest,
	setBuyerRequestActiveTab,
	setBuyerRequestFilterField
} from '@store/actions/BuyerRequestAction';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';
import { clearTeamFilter } from '@store/actions/TeamMemberAction';

import ApiClient from '@lib/apiClient';

import { isPageReachBottom } from '@utils/Util';

export const BuyerRequests = () => {
	const [allRequests, setAllRequests] = useState(null);
	const [page, setPage] = useState(0);
	const filterFields = useSelector((store) => store.buyerRequestStore);
	const [activeTab, setActiveTab] = useState(filterFields?.activeTab);
	const [isOpenDate, setIsOpenDate] = useState(false);
	const scrollRef = useRef({});
	const myTeamMembers = useSelector((store) => store.teamMembersReducer);
	const dispatch = useDispatch();
	const requestTypeList = [
		{ id: 'COLLECTION', value: 'Collection' },
		{ id: 'ORDER', value: 'Order' },
		{ id: 'QUOTES', value: 'Quotes' },
		{ id: 'SAMPLE', value: 'Sample' }
	];

	const setCurrentState = (data) => {
		scrollRef.current = data;
	};

	const getBuyerRequests = async (pageNumber) => {
		try {
			dispatch(openLoader());
			let params = `?page=${pageNumber}&status=${activeTab}&size=${20}&sort=requestedAt,desc`;
			if (filterFields.filterField?.requestedBy) {
				params += `&requestType=${filterFields?.filterField?.requestedBy?.id}`;
			}
			if (filterFields.filterField?.date) {
				params += `&requestedDate=${filterFields?.filterField?.date?.value}`;
			}
			const { data } = await ApiClient.GET(
				'fetchBuyerRequests',
				generateTeamParams(myTeamMembers, params)
			);
			dispatch(closeLoader());
			if (data) {
				if (pageNumber === 0) {
					setAllRequests(data);
					setCurrentState(data);
				} else {
					setAllRequests({ ...data, data: [...scrollRef.current.data, ...data.data] });
					setCurrentState({
						...data,
						data: [...scrollRef.current.data, ...data.data]
					});
				}
			}
		} catch ({ response }) {
			dispatch(closeLoader());
			if (response && response.data && response.data.message) {
				toastError(response.data.message);
			} else {
				toastError('Something went wrong! Please try again.');
			}
		}
	};

	useEffect(() => {
		window.scrollTo(0, 0);
		setPage(0);
		getBuyerRequests(0);
	}, [filterFields?.filterField, myTeamMembers]);

	useEffect(() => {
		if (page !== 0) {
			getBuyerRequests(page);
		}
	}, [page]);

	useEffect(() => {
		setActiveTab(filterFields?.activeTab);
	}, [filterFields?.activeTab]);

	useEffect(() => {
		document.addEventListener('scroll', handleScroll);
		return () => {
			document.removeEventListener('scroll', handleScroll);
		};
	}, []);

	const handleScroll = () => {
		if (isPageReachBottom()) {
			let { totalElements, data, currentPage } = scrollRef.current;
			if (totalElements > 0 && totalElements !== data.length) {
				setPage(currentPage + 1);
			}
		}
	};

	const handleDropDown = (item) => {
		dispatch(setBuyerRequestFilterField({ date: filterFields?.filterField?.date }, item));
		setPage(0);
	};

	const isShowClear = () => {
		return Object.keys(filterFields?.filterField)?.length > 0;
	};

	const onChangeTab = (tab) => {
		window.scrollTo(0, 0);
		dispatch(clearBuyerRequest());
		setActiveTab(tab);
		dispatch(setBuyerRequestActiveTab(tab));
		setPage(0);
	};

	return (
		<div>
			<div className="top-header bg-white">
				<div className="page-title-area">
					<span className="semibold-16">Pending requests</span>
				</div>
				<div className="navigation-tab shadow-nav-4dp">
					<ul>
						<li
							className={`${activeTab === 'PENDING' ? 'active' : ''}`}
							onClick={() => {
								// dispatch(setBuyerRequestActiveTab('PENDING'));
								onChangeTab('PENDING');
							}}
						>
							Pending
						</li>
						<li
							className={`${activeTab === 'APPROVED' ? 'active' : ''}`}
							onClick={() => {
								// dispatch(setBuyerRequestActiveTab('APPROVED'));
								onChangeTab('APPROVED');
							}}
						>
							Confirmed
						</li>
						<li
							className={`${activeTab === 'CANCELLED' ? 'active' : ''}`}
							onClick={() => {
								// dispatch(setBuyerRequestActiveTab('CANCELLED'));
								onChangeTab('CANCELLED');
							}}
						>
							Cancelled
						</li>
					</ul>
				</div>
				<div className="filter-container-top pt-2 pb-2 bg-gray">
					<div className="filters">
						<div className="samples-summary">
							<span>{allRequests?.totalElements} Requests</span>
						</div>
						<TeamChip />
					</div>
					<div className="filter-actions sample-dashboard-filter">
						{(isShowClear() || isTeamFilterExist(myTeamMembers)) && (
							<span
								className="clear-btn"
								onClick={() => {
									dispatch(clearTeamFilter());
									dispatch(clearBuyerRequest());
									setIsOpenDate(false);
									setPage(0);
								}}
							>
								Clear
							</span>
						)}
						<div className="single-filter">
							<CustomDropdown
								type="addItem"
								title={'Request'}
								onItemClick={handleDropDown}
								items={requestTypeList}
								selectedItem={filterFields?.filterField?.requestedBy?.value}
							/>
						</div>
						<UsersForTeamLead />
						<div className="single-filter">
							{isOpenDate ? (
								<input
									type="date"
									name="date"
									id="date"
									value={filterFields?.filterField?.date?.value || ''}
									onChange={(e) => {
										dispatch(
											setBuyerRequestFilterField(
												{ requestedBy: filterFields?.filterField?.requestedBy },
												e.target.value
											)
										);
										setPage(0);
									}}
								/>
							) : (
								<div className="select-component" onClick={() => setIsOpenDate(!isOpenDate)}>
									<div
										className="click-box click mb-1"
										// data-toggle="dropdown"
										// aria-haspopup="true"
										// aria-expanded="false"
									>
										<p className="regular-14">Date</p>
										<img src={`${process.env.PUBLIC_URL}/images/chevron_down.svg`} alt />
									</div>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
			<div className="sample-dashboard-container buyer-request-container bg-gray px-4">
				<div className="sample-items-row">
					{allRequests?.data?.length > 0 ? (
						allRequests?.data?.map((item, idx) => {
							if (item.requestType === 'SAMPLE')
								return (
									<SampleCard key={`sample_${idx}`} details={item.sampleRequestResponse} />
								);
							else if (item.requestType === 'QUOTES')
								return <QuotesCard key={`quote_${idx}`} details={item.quoteResponse} />;
							else if (item.requestType === 'COLLECTION')
								return (
									<CollectionCard
										key={`collection_${idx}`}
										details={item.moodBoardResponse}
									/>
								);
							else if (item.requestType === 'ORDER')
								return (
									<OrderCard
										key={`order_${idx}`}
										details={item.orderResponse}
										orderStatus={activeTab}
									/>
								);
							else return <></>;
						})
					) : (
						<NoData message="No request found" />
					)}
				</div>
			</div>
		</div>
	);
};
